import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Home, User, Mail, LogOut, LogIn, Search, ListOrdered, Truck, DollarSign, Settings,
} from "lucide-react";
import { getAuthUser, logout } from "@/lib/api";

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getAuthUser();
  const path = location.pathname;

  // Hide on standalone single-purpose forms so they feel focused.
  if (["/login", "/reset-password", "/verify-email"].includes(path)) return null;

  const handleLogoutBtn = () => {
    logout();
    navigate("/login");
  };

  const activeAdminTab = searchParams.get("tab") || "orders";
  const setAdminTab = (t) => navigate(`/admin?tab=${t}`, { replace: true });

  const scrollToContact = () => {
    const doScroll = () => {
      const el = document.querySelector('[data-testid="contact-admin-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (path !== "/mi-cuenta") {
      navigate("/mi-cuenta");
      setTimeout(doScroll, 350);
    } else {
      doScroll();
    }
  };

  let items = [];
  if (user?.role === "admin" && path.startsWith("/admin")) {
    items = [
      { key: "orders", label: "Órdenes", icon: ListOrdered, onClick: () => setAdminTab("orders"), active: activeAdminTab === "orders" },
      { key: "dispatches", label: "Despachos", icon: Truck, onClick: () => setAdminTab("dispatches"), active: activeAdminTab === "dispatches" },
      { key: "accounting", label: "Contab.", icon: DollarSign, onClick: () => setAdminTab("accounting"), active: activeAdminTab === "accounting" },
      { key: "settings", label: "Config", icon: Settings, onClick: () => setAdminTab("settings"), active: activeAdminTab === "settings" },
      { key: "logout", label: "Salir", icon: LogOut, onClick: handleLogoutBtn, danger: true },
    ];
  } else if (user?.role === "worker" && path.startsWith("/worker")) {
    items = [
      { key: "home", label: "Buscar", icon: Search, onClick: () => navigate("/worker"), active: true },
      { key: "logout", label: "Salir", icon: LogOut, onClick: handleLogoutBtn, danger: true },
    ];
  } else if (user?.role === "customer") {
    items = [
      { key: "home", label: "Nuevo pedido", icon: Home, onClick: () => navigate("/"), active: path === "/" },
      { key: "account", label: "Mi cuenta", icon: User, onClick: () => navigate("/mi-cuenta"), active: path === "/mi-cuenta" },
      { key: "contact", label: "Contacto", icon: Mail, onClick: scrollToContact },
      { key: "logout", label: "Salir", icon: LogOut, onClick: handleLogoutBtn, danger: true },
    ];
  } else {
    // Not authenticated
    items = [
      { key: "home", label: "Inicio", icon: Home, onClick: () => navigate("/"), active: path === "/" },
      { key: "login", label: "Entrar", icon: LogIn, onClick: () => navigate("/login"), active: false },
    ];
  }

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-white/10 bg-zinc-950/95 backdrop-blur-xl"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((it) => {
          const Icon = it.icon;
          const activeCls = it.active
            ? "text-yellow-400"
            : it.danger
            ? "text-zinc-400 active:text-red-400"
            : "text-zinc-400 active:text-white";
          return (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              data-testid={`mnav-${it.key}`}
              aria-current={it.active ? "page" : undefined}
              className={`relative flex flex-col items-center justify-center gap-1 py-2 min-h-[64px] transition-colors ${activeCls}`}
            >
              {it.active && (
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-yellow-400 rounded-b-full"
                />
              )}
              <Icon className="w-5 h-5" strokeWidth={it.active ? 2.5 : 2} />
              <span
                className={`text-[10px] leading-none tracking-wide truncate max-w-full px-1 ${
                  it.active ? "font-bold" : "font-medium"
                }`}
              >
                {it.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
